import type { CameraApiResponse } from "@src/services/api/cameras-api";
import { AssetId, CameraId, AssetCameraKey, type AssetSource, type Fleet } from "@visioni/a3s-schema";

export function getCameraKeyString(camera: CameraApiResponse): string {
  const key = camera.key;
  try {
    const asset = AssetId.fromString(key.asset);
    const cameraId = CameraId.fromString(key.id);
    const cameraKey = AssetCameraKey.fromParts(asset, cameraId);
    return AssetCameraKey.toString(cameraKey);
  } catch {
    return `${key.asset}@${key.id}`;
  }
}

export function getSiteString(camera: CameraApiResponse): string {
  return camera.site.id;
}

export function getSiteName(
  camera: CameraApiResponse,
  siteId: string,
  siteNames?: ReadonlyMap<string, string>,
): { name: string; subtitle?: string } {
  const name = siteNames?.get(siteId) ?? siteId;
  const subtitle = name !== siteId ? siteId : undefined;
  return { name, subtitle };
}

export function getCameraDisplayName(cameraKey: string): string {
  if (!cameraKey) return "";

  try {
    const key = AssetCameraKey.fromString(cameraKey);
    return CameraId.toString(AssetCameraKey.camera(key));
  } catch {
    return cameraKey;
  }
}

export function isLegacyAppCameraKey(cameraKey: string): boolean {
  try {
    const key = AssetCameraKey.fromString(cameraKey);
    const asset = AssetCameraKey.asset(key);
    const source = AssetId.source(asset);
    return source === "legacy_app";
  } catch {
    return false;
  }
}

export function isLegacyAppCamera(camera: CameraApiResponse): boolean {
  try {
    const asset = AssetId.fromString(camera.key.asset);
    const source = AssetId.source(asset);
    return source === "legacy_app";
  } catch {
    return false;
  }
}

/**
 * Formats a human-friendly camera label from a composite key.
 * "A2-G2-CAM_1111@camera-b" becomes "CAM_1111 - Camera B"
 * Legacy app camera keys are returned as is.
 */
export function formatCameraLabel(cameraKey: string): string {
  try {
    const key = AssetCameraKey.fromString(cameraKey);
    const asset = AssetCameraKey.asset(key);
    const camera = AssetCameraKey.camera(key);

    const cameraId = CameraId.toString(camera)
      .replace(/[-_]+/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

    // Asset ID of legacy app cameras can't be used for display
    if (AssetId.source(asset) === "legacy_app") {
      return "";
    }

    const assetNumber = AssetId.unique(asset);
    return `${assetNumber} - ${cameraId}`;
  } catch {
    return cameraKey;
  }
}

/**
 * Extracts the camera source and generation (fleet) from the camera's asset ID.
 * Returns an object with source and fleet, or undefined if extraction fails.
 * Relies on AssetId.toParts() - if that fails, returns undefined.
 */
export function getCameraSourceAndFleet(camera: CameraApiResponse): { source: AssetSource; fleet: Fleet } | undefined {
  try {
    const asset = AssetId.fromString(camera.key.asset);
    const parts = AssetId.toParts(asset);
    return {
      source: parts.source,
      fleet: parts.fleet,
    };
  } catch {
    return undefined;
  }
}
